import { Theme, ThemeUICSSObject } from 'theme-ui';
import { responsive, spacing } from '../../../../util';
import { getSelectedColors } from '../../../colors';
import { getSelectionStyles } from './getSelectionStyles';

export const clean: (theme: Theme) => ThemeUICSSObject = (theme) => {
  return {
    variant: 'colors.tables.primary',
    width: '100%',
    backgroundColor: 'components.cleanTable.body.backgroundColor',
    border: '1px solid',
    borderColor: 'components.cleanTable.borderColor',
    borderCollapse: 'inherit',
    borderSpacing: 0,
    tableLayout: 'fixed',
    selection__keyHandler: {
      maxHeight: '30vh',
    },

    thead: {
      zIndex: 1,
      position: 'sticky',
      insetBlockStart: 0,
      backgroundColor: 'palette.background.paper',

      'tr:not(.filtersRow):not(.responsiveTable__filters__row)': {
        'th, td': {
          variant: 'buttons.primary',
          width: 'breakWidth',
          backgroundColor: 'palette.background.paper',
          borderColor: 'palette.primary.dark',
          borderRadius: 0,
          borderStyle: 'solid',
          borderWidth: '1px',
          color: 'primary.text.primary',
          display: 'table-cell',
          verticalAlign: 'center',
          wordBreak: 'break-word',
          p: 0,
          px: 0,
          py: 0,
          border: 'none',
          borderBottom: '1px solid #ccc',

          '&[role="presentation"]': {
            display: 'none',
          },

          ...theme.palette.getStatesFromPath('components.cleanTable.header'),

          '& *': {
            color: 'inherit',
          },

          '.headButton__label': {
            fontWeight: 'bold',
            textAlign: 'left',
            p: 0,
            d: '',

            '& > *': {
              py: responsive({ 0: 3, 4: 4 }),
              px: 4,
            },
          },
          '.headButton__resizer': {
            width: '20px',
            cursor: 'ew-resize',
            zIndex: 1000,
            position: 'absolute',
            right: '-10px',
            height: '100%',
          },
          '&:last-of-type .headButton__resizer': {
            right: '0px',
          },

          '&.additionalColumn': {
            minWidth: '50px',
            maxWidth: '50px',
            width: '50px',
            p: 0,
            textAlign: 'center',
            verticalAlign: 'center',
          },

          '.headButton__container': {
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 3,

            svg: {
              flexShrink: 0,
            },
          },

          '&:not(:last-of-type)': {
            '&>div': {
              borderRightWidth: '1px',
            },
          },
        },
        'th.requiredFilterColumn': {
          borderLeft: '4px solid !important',
          borderLeftColor: 'palette.error.main !important',
        },
      },
      'tr.filtersRow, tr.responsiveTable__filters__row': {
        backgroundColor: 'components.cleanTable.body.backgroundColor',

        'th.noFilter': {
          background: 'transparent',
        },
        'td.requiredFilter__Border': {
          borderLeftStyle: 'solid !important',
          borderLeftWidth: '4px !important',
          borderLeftColor: 'palette.error.main',
        },

        td: {
          'input:not([disabled]), select:not([disabled])': {
            border: '1px solid white',
          },
          background: 'transparent',
          border: '1px solid',
          borderColor: 'components.cleanTable.body.borderColor',
          p: 0,
        },

        transition: 'height 0.3s',

        '&.hidden': {
          height: '0',
          overflow: 'hidden',
          p: spacing(0),
          border: 'none',

          '& *': {
            height: '0',
            overflow: 'hidden',
            fontSize: 0,
            lineHeight: 0,
            p: spacing(0),
            border: 'none',
          },
        },
      },
    },
    tbody: {
      tr: {
        ...theme.palette.getStatesFromPath('components.cleanTable.body', {
          states: {
            disabled: false,
            readonly: false,
            active: false,
            hover: false,
            checked: false,
            focus: false,
            path: false,
            selected: false,
          },
        }),
        '&:nth-of-type(2n)': {
          backgroundColor: 'components.cleanTable.body.evenRowsBackgroundColor',
        },

        '&.non-selectable': {
          cursor: 'not-allowed',
        },

        '&.hidden': {
          display: 'none',
        },

        '&.locked': {
          background: 'palette.gray.900',
          td: {
            borderColor: 'palette.gray.850',
          },
        },

        ...getSelectedColors(theme.palette),

        '&.draggingRow': {
          outline: '3px dotted',
          outlineColor: 'components.cleanTable.header.borderColor',
          outlineOffset: '-3px',

          '*': {
            outline: 'none !important',
          },
        },

        '.rowStatesList': {
          display: 'flex',
          gap: spacing(3),
          alignItems: 'center',

          svg: {
            width: 'smallIcon',
            height: 'smallIcon',
            cursor: 'pointer',
          },
        },

        td: {
          border: 'none',
          display: 'table-cell',
          p: 0,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          wordBreak: 'break-word',
          button: {
            height: 'fit-content',
            display: 'flex',
          },
          '&.stickyColumn': {
            position: 'sticky',
            left: 0,
            backgroundColor: 'components.cleanTable.body.backgroundColor',
          },

          '.moreInformationButton': {
            width: '30px',
            height: '30px',
            padding: '3px',
            border: 'none',
            background: 'transparent',
          },

          '.autoellipsis__container': {
            width: '100%',
          },

          '& > *': {
            py: spacing(3),
            px: 4,
          },
          borderBottomWidth: '1px',
          borderBottomStyle: 'solid',
          borderBottomColor: 'components.cleanTable.body.borderColor',
        },
      },

      '.responsiveTable__additionalInfoContainer': {
        background:
          'components.cleanTable.body.additionalInfoButton.backgroundColor',
        display: 'grid',
        gridTemplateColumns: responsive({
          0: 'repeat(2, 1fr)',
          5: 'repeat(4, 1fr)',
        }),
        columnGap: 3,
        rowGap: 2,
        '.listPanel__details': {
          pl: 6,
        },
        '.responsiveTable__additionalInfoItem': {
          whiteSpace: 'break-spaces',
          overflow: 'hidden',
          wordBreak: 'break-all',
        },
      },
      '.responsiveTable__additionalInfoContainer .responsiveTable__additionalInfoItem.separator':
        {
          gridColumnStart: '1',
          gridColumnEnd: '5',
          borderBottom: '1px solid',
          borderBottomColor: 'palette.gray.700',
          height: 'auto',
        },
    },
    '.editionMode': {
      border: '3px solid',
      borderColor: 'components.cleanTable.header.borderColor',
      borderWidth: '1.5px',
      borderStyle: 'dotted',
    },

    '.stateCell': {
      width: '50px',
      maxWidth: '50px',
      minWidth: '50px',
      textAlign: 'center',
      verticalAlign: 'middle',
    },

    ...getSelectionStyles(),
    '.no__registers': {
      py: 6,
      minWidth: '200px',
    },
    '.requiredFilter__Column': {
      fontWeight: 'bold',
    },

    '.bold': {
      fontWeight: 'bold',
    },

    '.light': {
      color: 'palette.gray.700',
    },
    '.additionalInfo__cell': {
      path: {
        fill: 'components.cleanTable.body.additionalInfoButton.color',
      },
    },

    '.additionalInfo__cell, .stateCell': {
      verticalAlign: 'middle',
    },
    '.isAcquired': {
      td: {
        div: {
          color: 'acquiredColor',
        },
      },
    },
  };
};
